package ai.jessie.web.onboarding

import ai.jessie.shared.onboarding.EntryDestination
import ai.jessie.shared.onboarding.EntryResponse
import ai.jessie.shared.onboarding.OrganisationChoiceResponse
import ai.jessie.shared.onboarding.OrganisationStartResponse
import ai.jessie.shared.onboarding.SelectOrganisationRequest
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class EntryUiState(
    val entry: EntryResponse? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
)

data class EntryActions(val refresh: () -> Unit, val select: (OrganisationChoiceResponse) -> Unit)

class EntryState(private val port: OnboardingPort, private val scope: CoroutineScope) {
    var state by mutableStateOf(EntryUiState())
        private set

    val actions = EntryActions(::refresh, ::select)

    fun refresh() = request { port.entry() }

    private fun select(choice: OrganisationChoiceResponse) = request {
        port.select(SelectOrganisationRequest(choice.organisationId, choice.role, choice.version))
    }

    private fun request(block: suspend () -> EntryResponse) {
        if (state.busy) return
        state = state.copy(busy = true, error = null)
        scope.launch {
            try {
                val entry = block().checked()
                currentCoroutineContext().ensureActive()
                state = state.copy(entry = entry)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: OnboardingRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = state.copy(entry = null, error = failure.problem())
                if (failure.refreshChoices()) refreshRejectedChoice()
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = state.copy(entry = null, error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private suspend fun refreshRejectedChoice() {
        try {
            val entry = port.entry().checked()
            currentCoroutineContext().ensureActive()
            state = state.copy(entry = entry)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: OnboardingRequestFailure) {
            currentCoroutineContext().ensureActive()
            state = state.copy(error = failure.problem())
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            state = state.copy(error = unavailableProblem())
        }
    }
}

data class OrganisationStartUiState(
    val data: OrganisationStartResponse? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
)

class OrganisationStartState(
    private val port: OnboardingPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
) {
    var state by mutableStateOf(OrganisationStartUiState())
        private set

    fun refresh() {
        if (state.busy) return
        state = state.copy(busy = true, error = null)
        scope.launch {
            try {
                val data = port.start(organisationId)
                currentCoroutineContext().ensureActive()
                require(data.organisationId.equals(organisationId, ignoreCase = true))
                state = state.copy(data = data)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: OnboardingRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = state.copy(data = null, error = failure.problem())
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = state.copy(data = null, error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }
}

internal fun EntryResponse.checked(): EntryResponse = also {
    if (destination == EntryDestination.ORGANISATION)
        require(validOrganisationId(organisationId.orEmpty()))
}

data class OnboardingProblem(
    val message: String,
    val fields: List<String> = emptyList(),
    val destination: String? = null,
    val destinationLabel: String? = null,
    val authenticationRequired: Boolean = false,
)

internal fun unavailableProblem() =
    OnboardingProblem("Laden oder Speichern fehlgeschlagen. Bitte erneut versuchen.")

/**
 * UC-006 A1. The same missing-organisation outcome for a route this client already rejects and for
 * the server's `ORGANISATION_REQUIRED`: no organisation information, only the way to selection.
 */
internal fun organisationRequiredProblem() =
    OnboardingProblem(
        "Ungültiger Organisationslink. Bitte wähle eine Organisation.",
        destination = "/organisations",
        destinationLabel = "Organisation auswählen",
    )

internal fun OnboardingRequestFailure.problem(): OnboardingProblem {
    return when (status) {
        HTTP_UNAUTHENTICATED ->
            OnboardingProblem(
                "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
                destination = "/api/v1/authentication/start",
                authenticationRequired = true,
            )
        HTTP_FORBIDDEN -> forbiddenProblem(code)
        // UC-006 A2, A4 and A6: one neutral absence that confirms nothing about existence.
        HTTP_NOT_FOUND ->
            OnboardingProblem("Die Organisation ist nicht verfügbar oder du hast keinen Zugriff.")
        HTTP_CONFLICT ->
            OnboardingProblem("Dein Organisationszugriff hat sich geändert. Bitte wähle erneut.")
        HTTP_BAD_REQUEST -> badRequestProblem(code, fields)
        // UC-006 A7: the action was not completed, so nothing may be presented as successful.
        HTTP_SERVER_ERROR ->
            if (code == "NOT_COMPLETED")
                OnboardingProblem(
                    "Die Aktion wurde nicht ausgeführt. Es wurde nichts geändert. Bitte erneut versuchen."
                )
            else unavailableProblem()
        else -> unavailableProblem()
    }
}

private fun forbiddenProblem(code: String) =
    when (code) {
        "ONBOARDING_REQUIRED" ->
            OnboardingProblem(
                "Bitte schliesse zuerst die Einrichtung ab.",
                destination = "/onboarding",
            )
        // UC-006 A3: the known action is denied without revealing anything further.
        "PERMISSION_DENIED" ->
            OnboardingProblem("Deine Rolle erlaubt diese Aktion in dieser Organisation nicht.")
        else -> OnboardingProblem("Diese Anfrage ist nicht erlaubt. Bitte lade die Seite erneut.")
    }

private fun badRequestProblem(code: String, fields: List<String>) =
    when (code) {
        "INVALID_PROFILE" ->
            OnboardingProblem("Bitte korrigiere die markierten Namen (je 1–250 Zeichen).", fields)
        "INVALID_CHOICE" ->
            OnboardingProblem(
                "Bitte prüfe die Organisationswahl und den Namen (1–200 Zeichen).",
                fields,
            )
        "ORGANISATION_REQUIRED" -> organisationRequiredProblem()
        else -> OnboardingProblem("Die Eingaben konnten nicht verarbeitet werden. Bitte prüfe sie.")
    }

internal const val HTTP_BAD_REQUEST = 400
internal const val HTTP_CONFLICT = 409
private const val HTTP_UNAUTHENTICATED = 401
internal const val HTTP_FORBIDDEN = 403
private const val HTTP_NOT_FOUND = 404
private const val HTTP_SERVER_ERROR = 500
